/**
 * The lists an agency keeps for itself.
 *
 * Three per-agency extensible lists were asked for inside a fortnight — who money is for, how a
 * payout was paid, and what stage a client is at — and each arrived with the same sentence attached:
 * *"be able to add to the list at a fly"*, *"they should be able to add custom payout methods"*,
 * *"agencies can choose from, and also be able to create client status"*. **Three implementations of
 * one idea is three screens that behave differently the day somebody renames an entry**, so this is
 * the one mechanism and everything that is genuinely a list of labels goes through it.
 *
 * ## What belongs here, and what deliberately does not
 *
 * An option is **a name the agency chose, and nothing else**. It has no behaviour, nothing hangs off
 * it, and the only questions anybody asks of it are "what is it called" and "is it still offered".
 * Payout methods and client statuses are exactly that.
 *
 * **Beneficiaries are not, and folding them in here would be the mistake this file exists to
 * prevent.** A beneficiary carries a kind, a party behind it, an email and a phone, and above all
 * `accrues` — whether money collected for it accumulates into a balance a payout run pays out. That
 * is a record with consequences, not a label; putting it in a table of names would mean either a
 * `metadata` column nobody can query or five nullable columns that are null for every other kind.
 * They share the *interaction* — a picker that can be added to where the choice is made — and not
 * the storage. See `beneficiaries.ts`.
 *
 * ## Added at the point of use, never on a settings screen
 *
 * Stated once here because it is the same rule for every list: the moment somebody needs a new
 * entry is the moment they are in the middle of something else — recording a charge, marking a
 * payout paid, filing a client. Sending them to a settings screen to come back is how a form gets
 * abandoned. The endpoints are therefore built to be called from a modal: the smallest possible
 * body, and an answer that can go straight back into the picker.
 */

/**
 * The lists themselves.
 *
 * Adding one is a value here, a starting list below, and nothing else — no table, no route, no
 * ownership entry. That is the whole return on making it one mechanism.
 */
export const ORG_OPTION_KINDS = ["payout_method", "client_status"] as const;
export type OrgOptionKind = (typeof ORG_OPTION_KINDS)[number];

/**
 * How an option is drawn where it is drawn as a mark.
 *
 * A closed set of four rather than a colour, because the agency picking a hex code is the agency
 * being asked to design the product — and a colour chosen against a light theme is unreadable on a
 * dark one. These are meanings; the application maps them to its own tokens.
 *
 * - `neutral` — no comment. The default, and what most entries are.
 * - `active` — a live, working relationship.
 * - `attention` — somebody has to do something.
 * - `closed` — finished with; still on the record, not in play.
 */
export const OPTION_TONES = ["neutral", "active", "attention", "closed"] as const;
export type OptionTone = (typeof OPTION_TONES)[number];

export interface OrgOptionSeed {
  name: string;
  tone: OptionTone;
}

/**
 * What a new agency's lists start as.
 *
 * **Every word obeys the register rule** in `09-CONVENTIONS.md`: this is read beside a tenancy
 * agreement and a receipt, so it is written as a well-kept ledger writes — precise, calm,
 * unembellished. "Lead", not "Hot lead!"; "Former", not "Gone cold".
 *
 * Deliberately short. A starting list is a suggestion, and a long one is a list somebody has to read
 * and dismiss before they can add the entry they actually wanted. Four statuses describe the whole
 * arc of a relationship and the agency adds its own.
 *
 * **A client status is one value, not a set**, and the four below are points on one axis for that
 * reason. See the note on `parties.status_option_id`.
 */
export const STARTING_OPTIONS: Record<OrgOptionKind, OrgOptionSeed[]> = {
  /* The ways money actually leaves the building. Bank transfer first because it is nearly all of
     them, and because the operator named it: "most likely when they pay via bank transfer". */
  payout_method: [
    { name: "Bank transfer", tone: "neutral" },
    { name: "Cash", tone: "neutral" },
    { name: "Cheque", tone: "neutral" },
  ],
  /*
    Where a relationship stands, in four words.

    `Lead` — somebody the agency is working and has not yet let to or for.
    `Active` — a live tenancy, or a property under management.
    `Dormant` — a real client with nothing running at the moment. Distinct from `Former`, which is
      the point: a landlord between tenants is not a landlord who has left.
    `Former` — finished with. On the record, not in play.
  */
  client_status: [
    { name: "Lead", tone: "attention" },
    { name: "Active", tone: "active" },
    { name: "Dormant", tone: "neutral" },
    { name: "Former", tone: "closed" },
  ],
};

/** What each list is called where a person reads its name. Plain and formal, like everything else. */
export const ORG_OPTION_LABELS: Record<OrgOptionKind, { one: string; many: string }> = {
  payout_method: { one: "Payout method", many: "Payout methods" },
  client_status: { one: "Client status", many: "Client statuses" },
};

export function isOrgOptionKind(value: unknown): value is OrgOptionKind {
  return typeof value === "string" && (ORG_OPTION_KINDS as readonly string[]).includes(value);
}

export function isOptionTone(value: unknown): value is OptionTone {
  return typeof value === "string" && (OPTION_TONES as readonly string[]).includes(value);
}

/**
 * How long a name may be.
 *
 * Sixty, the same as a charge's label and for the same reason: it is a table cell, a picker row and
 * an indicator beside somebody's name on a phone. A paragraph typed here would be truncated by every
 * place it appears rather than by the one place it is entered.
 */
export const OPTION_NAME_MAX = 60;

/**
 * The name as it should be stored, or null if there is nothing there.
 *
 * Trimmed and capped on the way in as well as here, so nothing longer is ever written — a 422 that
 * says what is wrong beats a silent trim that hands somebody back a name they did not type.
 */
export function normaliseOptionName(name: string | null | undefined): string | null {
  const trimmed = (name ?? "").trim().slice(0, OPTION_NAME_MAX).trim();
  return trimmed || null;
}
